import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingFallback } from '../common/LoadingFallback';
import { mobileDebugger } from '@/utils/mobileDebugger';
import { supabase } from '@/integrations/supabase/client';
import { getLogger } from '@/utils/logger';

import { needsProfileCompletion } from '@/utils/profile-completion';

const log = getLogger({ module: 'RootRedirect' });

export const RootRedirect = () => {
  const { user, profile, isLoading, isInitialized, refreshSession } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);
  
  // Helper function to check if we're on a profile creation page
  const isProfileCreationPage = location.pathname === "/create-username" || location.pathname === "/create-profile";
  
  // Check if user needs profile completion using the shared utility
  const userNeedsProfileCompletion = needsProfileCompletion(profile);

  // Remove duplicate session checks - let AuthContext handle all session management
  useEffect(() => {
    // Only wait for auth context to initialize
    setIsChecking(false);
  }, []);


  useEffect(() => {
    if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
      mobileDebugger.logAuthEvent('route_change', {
        component: 'RootRedirect',
        path: location.pathname,
        hasUser: !!user,
        hasProfile: !!profile,
        isLoading,
        isInitialized
      });
    }
  }, [location.pathname, user, profile, isLoading, isInitialized]);

  log.info('RootRedirect - Auth State:', { 
    user: user ? { id: user.id } : null, 
    hasProfile: !!profile,
    isLoading,
    isInitialized,
    pathname: location.pathname,
    isAdmin: profile?.is_admin,
    username: profile?.username
  });

  // Still checking session or waiting for initialization
  if (isChecking || !isInitialized) {
    log.info('RootRedirect - Waiting for initialization or session check...');
    return <LoadingFallback />;
  }

  // Simplified: rely on AuthContext state only
  if (!user) {
    log.info('RootRedirect - No user, redirecting to home');
    return <Navigate to="/home" replace state={{ from: location }} />;
  }

  // If we're at root path with user, check username
  if (location.pathname === '/') {
    // If user needs profile completion, redirect to create-username
    if (user && userNeedsProfileCompletion) {
      log.info('RootRedirect - User needs profile completion, redirecting to create-username');
      return <Navigate to="/create-username" replace />;
    }
    // Otherwise go to bulletins
    log.info('RootRedirect - User authenticated, redirecting to bulletins');
    return <Navigate to="/bulletins" replace />;
  }

  log.info('RootRedirect - Not at root path, allowing current navigation');
  return null;
};
